import React from 'react';
import { Modal, Image, Typography, Divider, Tag, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { NewsItem } from '../../api/services/newsService';

const { Title, Paragraph, Text } = Typography;

interface NewsDetailModalProps {
  visible: boolean;
  onClose: () => void;
  news: NewsItem | null;
  loading?: boolean;
}

const NewsDetailModal: React.FC<NewsDetailModalProps> = ({
  visible,
  onClose,
  news,
  loading = false,
}) => {
  const { t } = useTranslation();

  if (!news) return null;

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      centered
      className="news-detail-modal"
      styles={{
        body: { padding: 0 },
      }}
    >
      <div className="news-detail-content">
        {news.preview && (
          <div className="news-image-container">
            <Image
              src={news.preview}
              alt={news.title}
              className="h-64 w-full object-cover"
              preview={false}
            />
          </div>
        )}

        <div className="p-6">
          <div className="mb-4">
            <Space direction="vertical" size="small" className="w-full">
              <div className="flex items-center justify-between">
                <Tag color="blue" className="text-xs">
                  {t('news')}
                </Tag>
                <Text type="secondary" className="text-sm">
                  {new Date(Number(news.createTime)).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </Text>
              </div>
            </Space>
          </div>

          <Title level={2} className="mb-4 text-gray-800">
            {news.title}
          </Title>

          <Paragraph className="mb-6 text-lg leading-relaxed text-gray-600">
            {news.summary}
          </Paragraph>

          <Divider />

          <div className="news-description">
            <Paragraph className="whitespace-pre-wrap leading-relaxed text-gray-700">
              {news.description}
            </Paragraph>
          </div>

          <Divider />

          <div className="flex items-center justify-between text-sm text-gray-500">
            <div>
              <Text type="secondary">
                {t('publishedOn')}: {new Date(Number(news.createTime)).toLocaleDateString()}
              </Text>
            </div>
            {news.updateTime !== news.createTime && (
              <div>
                <Text type="secondary">
                  {t('updatedOn')}: {new Date(Number(news.updateTime)).toLocaleDateString()}
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .news-detail-modal .ant-modal-content {
          border-radius: 12px;
          overflow: hidden;
        }

        .news-detail-content {
          max-height: 80vh;
          overflow-y: auto;
        }

        .news-image-container {
          position: relative;
          overflow: hidden;
        }

        .news-description {
          min-height: 100px;
        }
      `}</style>
    </Modal>
  );
};

export default NewsDetailModal;
